<template>
  <div v-if="chain">
    <CCard>
      <CCardHeader>
        <strong>{{ chain.scope === 'global' ? '🌐' : '📍' }} {{ chain.display_name || chain.name }}</strong>
        <small class="text-muted ml-2">下发 Chain：{{ chain.name }}</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="$router.back()">返回</CButton>
            <CButton color="info" size="sm" @click="openEditModal">编辑信息</CButton>
            <CButton color="success" size="sm" @click="syncChain">同步此 chain</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">地址族</small>
              <br><strong>{{ chain.afi }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">协议</small>
              <br><strong>{{ chain.protocol }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">方向</small>
              <br><strong>{{ chain.direction }}</strong>
            </CCallout>
          </CCol>
          <CCol sm="3">
            <CCallout color="info">
              <small class="text-muted">Scope</small>
              <br><strong>{{ chain.scope }}</strong>
            </CCallout>
          </CCol>
        </CRow>

        <p v-if="chain.description">{{ chain.description }}</p>

        <!-- chain 关联路由器（global / local 均需分组关联） - 穿梭框样式 -->
        <div class="mb-3">
          <h6>关联路由器
            <small class="text-muted ml-2">
              {{ chain.scope === 'global' ? '全局 Chain 需关联到指定路由器组才会同步' : '本机 Chain 关联到指定路由器' }}
            </small>
          </h6>
          <div class="d-flex align-items-center router-transfer">
            <!-- 左侧：可选路由器 -->
            <div class="transfer-panel">
              <div class="transfer-header">可选路由器 ({{ availableRouters.length }})</div>
              <div class="transfer-list">
                <div
                  v-for="r in availableRouters"
                  :key="r.id"
                  class="transfer-item"
                  :class="{ active: leftSelected.includes(r.id) }"
                  @click="toggleLeftSelect(r.id)"
                >
                  {{ r.name }}
                  <small class="text-muted ml-1">{{ r.host }}</small>
                </div>
                <div v-if="availableRouters.length === 0" class="transfer-empty">全部已选</div>
              </div>
            </div>
            <!-- 中间：移动按钮 -->
            <div class="transfer-actions">
              <CButtonGroup vertical>
                <CButton color="primary" size="sm" :disabled="leftSelected.length === 0" @click="moveRight">添加 &raquo;</CButton>
                <CButton color="success" size="sm" :disabled="rightSelected.length === 0" @click="moveLeft">&laquo; 移除</CButton>
              </CButtonGroup>
            </div>
            <!-- 右侧：已选路由器 -->
            <div class="transfer-panel">
              <div class="transfer-header">已关联 ({{ selectedRouterIds.length }})</div>
              <div class="transfer-list">
                <div
                  v-for="r in selectedRouters"
                  :key="r.id"
                  class="transfer-item"
                  :class="{ active: rightSelected.includes(r.id) }"
                  @click="toggleRightSelect(r.id)"
                >
                  {{ r.name }}
                  <small class="text-muted ml-1">{{ r.host }}</small>
                </div>
                <div v-if="selectedRouters.length === 0" class="transfer-empty">暂未关联</div>
              </div>
            </div>
          </div>
          <div class="mt-2" v-if="routersDirty">
            <CButtonGroup>
              <CButton color="success" size="sm" @click="saveRouters">保存关联</CButton>
              <CButton color="danger" size="sm" @click="resetRouters">取消</CButton>
            </CButtonGroup>
          </div>
        </div>

        <!-- 已关联路由器接管操作 -->
        <div v-if="chain.routers && chain.routers.length" class="mb-3">
          <h6>关联路由器操作</h6>
          <CAlert color="info" fade>
            「同步」将模板规则推送到路由器，相同内容的手动规则会自动打标认领。「接管」仅打标不同步。
          </CAlert>
          <table class="table table-sm">
            <thead>
              <tr>
                <th>路由器</th>
                <th width="220">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in chain.routers" :key="r.id">
                <td>{{ r.name }} <small class="text-muted">{{ r.host }}</small></td>
                <td>
                  <CButtonGroup>
                    <CButton color="success" size="sm" :disabled="syncLoading === r.id" @click="syncSingleRouter(r)">
                      {{ syncLoading === r.id ? '同步中...' : '同步' }}
                    </CButton>
                    <CButton color="warning" size="sm" :disabled="claimLoading === r.id" @click="claimRouter(r)">
                      {{ claimLoading === r.id ? '接管中...' : '接管' }}
                    </CButton>
                  </CButtonGroup>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr>

        <h6>Filter Rules
          <CButton color="primary" size="sm" class="ml-2" @click="openRuleModal()">新增规则</CButton>
        </h6>

        <table class="table table-sm">
          <thead>
            <tr>
              <th width="60">#</th>
              <th>规则脚本</th>
              <th width="120">描述</th>
              <th width="100">状态</th>
              <th width="180">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in rules" :key="r.id">
              <td>{{ idx + 1 }}</td>
              <td><code>{{ r.rule_script }}</code></td>
              <td>{{ r.description }}</td>
              <td>
                <CBadge :color="r.enabled ? 'success' : 'secondary'">
                  {{ r.enabled ? '启用' : '禁用' }}
                </CBadge>
              </td>
              <td>
                <CButtonGroup>
                  <CButton color="primary" size="sm" :disabled="idx === 0" @click="moveRule(idx, -1)">上移</CButton>
                  <CButton color="primary" size="sm" :disabled="idx === rules.length - 1" @click="moveRule(idx, 1)">下移</CButton>
                  <CButton color="success" size="sm" @click="openRuleModal(r, idx)">编辑</CButton>
                  <CButton color="warning" size="sm" @click="toggleRule(r)">{{ r.enabled ? '禁用' : '启用' }}</CButton>
                  <CButton color="danger" size="sm" @click="deleteRule(r, idx)">删除</CButton>
                </CButtonGroup>
              </td>
            </tr>
            <tr v-if="rules.length === 0">
              <td colspan="5" class="text-center text-muted">暂无规则，点击右上角"新增规则"创建</td>
            </tr>
          </tbody>
        </table>
      </CCardBody>
    </CCard>

    <!-- 规则编辑 Modal -->
    <CModal :show.sync="ruleModal.show" :title="ruleModal.isEdit ? '编辑规则' : '新增规则'" color="primary" size="xl">
      <CForm>
        <CTextarea
          label="规则脚本 (RouterOS routing filter rule)"
          v-model="ruleModal.form.rule_script"
          :rows="10"
          placeholder='if (dst in 192.168.0.0/16 && dst-len in 16-32) { accept; }'
        />
        <CInput label="描述" v-model="ruleModal.form.description" />
        <CAlert color="info" fade>
          <strong>语法提示：</strong><br>
          匹配器：<code>dst</code>, <code>dst-len</code>, <code>protocol</code>, <code>afi</code>,
          <code>bgp-as-path</code>, <code>bgp-path-len</code>, <code>bgp-local-pref</code>,
          <code>bgp-med</code>, <code>bgp-network</code>, <code>rpki</code>, <code>ospf-type</code><br>
          动作：<code>accept</code>, <code>reject</code>, <code>jump</code>, <code>return</code>,
          <code>set</code>, <code>unset</code>, <code>append</code>, <code>delete</code><br>
          可设置属性：<code>distance</code>, <code>bgp-local-pref</code>, <code>bgp-med</code>,
          <code>bgp-out-med</code>, <code>bgp-path-prepend</code>, <code>bgp-weight</code>,
          <code>bgp-communities</code>, <code>ospf-ext-metric</code>, <code>ospf-ext-type</code>
        </CAlert>
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="ruleModal.show = false">取消</CButton>
          <CButton color="success" @click="submitRule">保存</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 编辑 Chain 信息 Modal -->
    <CModal :show.sync="editModal.show" title="编辑 Chain 信息" color="info">
      <CForm>
        <CInput label="下发 Chain 名（同步到路由器的名称，不可修改）" :value="chain ? chain.name : ''" disabled />
        <CInput
          label="显示名称（系统展示用）"
          v-model="editModal.form.display_name"
          placeholder="留空则与下发 Chain 名相同"
        />
        <CTextarea label="描述" v-model="editModal.form.description" :rows="2" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="editModal.show = false">取消</CButton>
          <CButton color="success" @click="submitEdit">保存</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="primary">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="danger" @click="confirmModal.show = false">取消</CButton>
          <CButton color="success" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RoutingFilterDetail',
  data() {
    return {
      chain: null,
      rules: [],
      allRouters: [],
      selectedRouterIds: [],
      originalRouterIds: [],
      routersDirty: false,
      leftSelected: [],
      rightSelected: [],
      claimLoading: null,
      syncLoading: null,
      confirmModal: {
        show: false,
        title: '确认',
        message: '',
        callback: () => {}
      },
      ruleModal: {
        show: false,
        isEdit: false,
        editId: null,
        editIndex: null,
        form: { rule_script: '', description: '' }
      },
      editModal: {
        show: false,
        form: { display_name: '', description: '' }
      }
    }
  },
  mounted() {
    this.loadChain()
    this.loadAllRouters()
  },
  computed: {
    availableRouters() {
      return this.allRouters.filter(r => !this.selectedRouterIds.includes(r.id))
    },
    selectedRouters() {
      return this.allRouters.filter(r => this.selectedRouterIds.includes(r.id))
    }
  },
  methods: {
    async loadChain() {
      try {
        const res = await this.$axios.get(`user/routing/filter/chains/${this.$route.params.id}`)
        if (res.data.code === 200) {
          this.chain = res.data.data
          this.rules = res.data.data.rules || []
          this.selectedRouterIds = (res.data.data.routers || []).map(r => r.id)
          this.originalRouterIds = [...this.selectedRouterIds]
          this.$store.commit('set', ['breadcrumbLabel', this.chain.display_name || this.chain.name])
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    openEditModal() {
      this.editModal.form.display_name = (this.chain && this.chain.display_name) || ''
      this.editModal.form.description = (this.chain && this.chain.description) || ''
      this.editModal.show = true
    },
    async submitEdit() {
      if (!this.chain) return
      try {
        const res = await this.$axios.post(
          `user/routing/filter/chains/${this.chain.id}/edit`,
          this.editModal.form
        )
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.editModal.show = false
          this.loadChain()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async loadAllRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    isRouterSelected(id) {
      return this.selectedRouterIds.includes(id)
    },
    toggleRouter(id) {
      const i = this.selectedRouterIds.indexOf(id)
      if (i >= 0) this.selectedRouterIds.splice(i, 1)
      else this.selectedRouterIds.push(id)
      this.routersDirty = JSON.stringify(this.selectedRouterIds.sort()) !== JSON.stringify(this.originalRouterIds.sort())
    },
    toggleLeftSelect(id) {
      const i = this.leftSelected.indexOf(id)
      if (i >= 0) this.leftSelected.splice(i, 1)
      else this.leftSelected.push(id)
    },
    toggleRightSelect(id) {
      const i = this.rightSelected.indexOf(id)
      if (i >= 0) this.rightSelected.splice(i, 1)
      else this.rightSelected.push(id)
    },
    moveRight() {
      this.selectedRouterIds.push(...this.leftSelected)
      this.leftSelected = []
      this.routersDirty = JSON.stringify(this.selectedRouterIds.sort()) !== JSON.stringify(this.originalRouterIds.sort())
    },
    moveLeft() {
      this.selectedRouterIds = this.selectedRouterIds.filter(id => !this.rightSelected.includes(id))
      this.rightSelected = []
      this.routersDirty = JSON.stringify(this.selectedRouterIds.sort()) !== JSON.stringify(this.originalRouterIds.sort())
    },
    resetRouters() {
      this.selectedRouterIds = [...this.originalRouterIds]
      this.leftSelected = []
      this.rightSelected = []
      this.routersDirty = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    async syncSingleRouter(r) {
      this.showConfirm('确认同步', `确认同步模板到 ${r.name}？`, async () => {
        this.syncLoading = r.id
        try {
          const res = await this.$axios.post(`user/routing/filter/sync/chain/${this.chain.id}/router/${r.id}`)
          if (res.data.code === 200) {
            const results = res.data.data.results || []
            const d = results[0] || {}
            const failed = d.failed || 0
            const success = d.success || 0
            this.$toasted.show(`${r.name} 同步完成：成功 ${success}，失败 ${failed}`, {
              type: failed > 0 ? 'warning' : 'success'
            })
          } else {
            this.$toasted.show(res.data.msg || '同步失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
        this.syncLoading = null
      })
    },
    async claimRouter(r) {
      this.showConfirm('确认接管', `确认接管 ${r.name} 上 chain "${this.chain.name}" 的已有规则？系统会按内容和顺序匹配模板规则，给匹配到的规则打上 [managed] 标记。`, async () => {
        this.claimLoading = r.id
        try {
          const res = await this.$axios.post(`user/routing/filter/chains/${this.chain.id}/claim`, {
            router_id: r.id,
            mode: 'auto'
          })
          if (res.data.code === 200) {
            const d = res.data.data
            const msg = `接管完成：路由器规则 ${d.total_router_rules} 条，模板规则 ${d.total_template_rules} 条，成功标记 ${d.marked} 条` + (d.failed > 0 ? `，失败 ${d.failed} 条` : '')
            this.$toasted.show(msg, { type: d.failed > 0 ? 'warning' : 'success' })
          } else {
            this.$toasted.show(res.data.msg || '接管失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
        this.claimLoading = null
      })
    },
    async saveRouters() {
      try {
        const res = await this.$axios.post(
          `user/routing/filter/chains/${this.chain.id}/routers`,
          { router_ids: this.selectedRouterIds }
        )
        if (res.data.code === 200) {
          this.$toasted.show('关联已保存', { type: 'success' })
          this.originalRouterIds = [...this.selectedRouterIds]
          this.routersDirty = false
          this.leftSelected = []
          this.rightSelected = []
          await this.loadChain()
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    openRuleModal(rule, idx) {
      if (rule) {
        this.ruleModal.isEdit = true
        this.ruleModal.editId = rule.id
        this.ruleModal.editIndex = idx
        this.ruleModal.form = {
          rule_script: rule.rule_script,
          description: rule.description || ''
        }
      } else {
        this.ruleModal.isEdit = false
        this.ruleModal.editId = null
        this.ruleModal.editIndex = null
        this.ruleModal.form = { rule_script: '', description: '' }
      }
      this.ruleModal.show = true
    },
    async submitRule() {
      const chainId = this.chain.id
      try {
        let res
        if (this.ruleModal.isEdit) {
          res = await this.$axios.post(
            `user/routing/filter/rules/${this.ruleModal.editId}/edit`,
            this.ruleModal.form
          )
        } else {
          res = await this.$axios.post(
            `user/routing/filter/chains/${chainId}/rules`,
            this.ruleModal.form
          )
        }
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.ruleModal.show = false
          this.loadChain()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async toggleRule(r) {
      try {
        const res = await this.$axios.post(`user/routing/filter/rules/${r.id}/toggle`)
        if (res.data.code === 200) {
          r.enabled = res.data.data.enabled
          this.$toasted.show('已切换', { type: 'success' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async deleteRule(r, idx) {
      this.showConfirm('确认删除', `确认删除规则 #${idx + 1}？`, async () => {
        try {
          const res = await this.$axios.delete(`user/routing/filter/rules/${r.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadChain()
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    async moveRule(idx, direction) {
      const newIdx = idx + direction
      if (newIdx < 0 || newIdx >= this.rules.length) return
      // 交换位置
      const arr = [...this.rules]
      const tmp = arr[idx]
      arr[idx] = arr[newIdx]
      arr[newIdx] = tmp
      const ruleIds = arr.map(r => r.id)
      try {
        const res = await this.$axios.post(`user/routing/filter/chains/${this.chain.id}/rules/reorder`, { rule_ids: ruleIds })
        if (res.data.code === 200) {
          this.loadChain()
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
    },
    async syncChain() {
      this.showConfirm('确认同步', '确认同步此 chain 到所有目标路由器？', async () => {
        try {
          const res = await this.$axios.post(`user/routing/filter/sync/chain/${this.chain.id}`)
          if (res.data.code === 200) {
            const results = res.data.data.results || []
            const failed = results.reduce((s, r) => s + (r.failed || 0), 0)
            this.$toasted.show(`同步完成，路由器数: ${results.length}，失败操作: ${failed}`, {
              type: failed > 0 ? 'warning' : 'success'
            })
          } else {
            this.$toasted.show(res.data.msg || '同步失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.table td,
.table th {
  vertical-align: middle !important;
}
.router-transfer {
  gap: 8px;
}
.transfer-panel {
  width: 260px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.transfer-header {
  padding: 8px 12px;
  background: #f0f3f5;
  border-bottom: 1px solid #d8dbe0;
  font-weight: 600;
  font-size: 13px;
  color: #4f5d73;
}
.transfer-list {
  max-height: 240px;
  min-height: 160px;
  overflow-y: auto;
  padding: 4px 0;
}
.transfer-item {
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.15s;
}
.transfer-item:hover {
  background: #e9ecef;
}
.transfer-item.active {
  background: #321fdb;
  color: #fff;
}
.transfer-item.active small {
  color: #c8d3ff !important;
}
.transfer-empty {
  padding: 20px;
  text-align: center;
  color: #9da6b0;
  font-size: 13px;
}
.transfer-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
